/**
 * Golf Card Game Types
 *
 * This file defines all TypeScript types and interfaces for the Golf card game implementation.
 * It follows the design specified in .kiro/specs/golf-card-game/design.md
 */

// ============================================================================
// Card Types
// ============================================================================

/**
 * Card suits in a standard deck
 */
export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

/**
 * Card ranks from Ace through King
 */
export type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

/**
 * A playing card with suit and rank
 */
export interface Card {
  suit: Suit;
  rank: Rank;
}

/**
 * Get the point value of a card.
 * Card value mapping: A=1, 2-10=face value, J=10, Q=10, K=0
 */
export function getCardValue(card: Card): number {
  switch (card.rank) {
    case 'A': return 1;
    case '2': return 2;
    case '3': return 3;
    case '4': return 4;
    case '5': return 5;
    case '6': return 6;
    case '7': return 7;
    case '8': return 8;
    case '9': return 9;
    case '10': return 10;
    case 'J': return 10;
    case 'Q': return 10;
    case 'K': return 0;
  }
}

// ============================================================================
// Grid Types
// ============================================================================

/**
 * Position in the 2x3 card grid
 * row: 0-1, col: 0-2
 */
export interface GridPosition {
  row: number;  // 0-1
  col: number;  // 0-2
}

/**
 * A single cell in the card grid containing a card and its visibility state
 */
export interface GridCell {
  card: Card;
  faceUp: boolean;
}

/**
 * CardGrid is a 2x3 array: [row][col]
 */
export type CardGrid = GridCell[][];

// ============================================================================
// Game State Types
// ============================================================================

/**
 * Phase of the current round
 */
export type RoundPhase = 'initial-reveal' | 'playing' | 'final-turns' | 'scoring' | 'round-complete';

/**
 * Phase within a single turn
 */
export type TurnPhase = 'draw' | 'place-or-discard' | 'flip' | 'complete';

/**
 * AI difficulty levels
 */
export type GolfDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Game status with discriminated union
 */
export type GolfGameStatus =
  | { type: 'waiting-for-players' }
  | { type: 'initial-reveal' }
  | { type: 'active' }
  | { type: 'round-end'; roundNumber: number }
  | { type: 'game-over'; winner: number | number[]; finalScores: number[] };

/**
 * State for a single player
 */
export interface PlayerState {
  grid: CardGrid;
  initialRevealsComplete: boolean;
  hasKnocked: boolean;
}

/**
 * Complete game state for persistence, synchronization, and serialization
 */
export interface GolfGameState {
  // Players
  players: PlayerState[];
  playerCount: number;
  playerNames: string[];

  // Round tracking
  currentRound: number;       // 1-9
  totalRounds: number;        // Always 9
  roundPhase: RoundPhase;

  // Turn tracking
  currentPlayerIndex: number;
  turnPhase: TurnPhase;
  drawnCard: Card | null;
  drawSource: 'draw-pile' | 'discard-pile' | null;

  // Knock state
  knockerIndex: number | null;
  finalTurnsRemaining: number;  // Players who still get a final turn

  // Piles
  drawPile: Card[];
  discardPile: Card[];

  // Scoring
  scorecard: Scorecard;

  // Game metadata
  gameMode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: GolfDifficulty;
  gameStatus: GolfGameStatus;
  timestamp: number;
}

// ============================================================================
// Game Settings
// ============================================================================

/**
 * Settings for starting a new game
 */
export interface GolfGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: GolfDifficulty;
  playerCount: number;  // 2-4
  playerNames?: string[];
  isPrivate?: boolean;
}

// ============================================================================
// Move Types
// ============================================================================

/**
 * Representation of a game move (discriminated union)
 */
export type GolfMove =
  | { type: 'initial-reveal'; positions: GridPosition[] }
  | { type: 'draw'; source: 'draw-pile' | 'discard-pile' }
  | { type: 'replace'; position: GridPosition; drawnCard: Card; replacedCard: Card }
  | { type: 'discard'; drawnCard: Card; flippedPosition: GridPosition }
  | { type: 'knock' };

/**
 * Result of a card replacement action
 */
export interface ReplaceResult {
  success: boolean;
  replacedCard?: Card;
  error?: string;
}

// ============================================================================
// Scoring Types
// ============================================================================

/**
 * Scorecard tracking all rounds for all players
 */
export interface Scorecard {
  roundScores: (RoundScoreBreakdown | null)[][];  // [playerIndex][roundIndex]
  cumulativeScores: number[];                      // [playerIndex]
}

/**
 * Detailed breakdown of a single round's score for one player
 */
export interface RoundScoreBreakdown {
  cardValues: number[][];           // [row][col] individual card values
  columnCancellations: number[];    // Column indices where pairs cancel
  subtotal: number;                 // Sum before penalty
  knockPenalty: number;             // 10 if knocked and not lowest
  total: number;                    // Final round score
}

/**
 * Information about a column-pair cancellation
 */
export interface ColumnCancellation {
  columnIndex: number;
  matchingRank: Rank;
  cancelledValue: number;  // Points saved by cancellation
}

// ============================================================================
// Statistics Types
// ============================================================================

/**
 * Statistics for a specific game mode/difficulty
 */
export interface GolfGameStats {
  wins: number;
  losses: number;
  ties: number;
  averageRoundScore: number;
  averageGameScore: number;
  bestRoundScore: number;
  bestGameScore: number;
  gamesPlayed: number;
}

/**
 * Complete golf game statistics
 */
export interface GolfStatistics {
  userId?: string;
  singlePlayer: {
    easy: GolfGameStats;
    medium: GolfGameStats;
    hard: GolfGameStats;
  };
  localMultiplayer: GolfGameStats;
  onlineMultiplayer: GolfGameStats;
  totalGames: number;
  totalRounds: number;
  totalPlayTime: number;
}

// ============================================================================
// AI Types
// ============================================================================

/**
 * AI action decision (discriminated union)
 */
export type AIAction =
  | { type: 'replace'; position: GridPosition }
  | { type: 'discard' };
